import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { EnhancedIncomingCallComponent, EnhancedIncomingCallData } from '../shared/enhanced-incoming-call/enhanced-incoming-call.component';
import { CallAudioService } from './call-audio.service';
import { GlobalCallContextService } from './global-call-context.service';
import { GlobalCallPanelService } from './global-call-panel.service';

export interface CallModalResult {
  reason: 'answered' | 'declined' | 'ended' | 'timeout' | 'dismissed' | string;
  leadInfo?: any;
  duration?: number;
  wasAnswered?: boolean;
  callId?: string;
  phoneNumber?: string;
  leadId?: number;
}

@Injectable({
  providedIn: 'root'
})
export class EnhancedCallModalService {
  private dialog = inject(MatDialog);
  private audioService = inject(CallAudioService);
  private globalCallContext = inject(GlobalCallContextService);
  private globalCallPanel = inject(GlobalCallPanelService);

  private activeModalRef: MatDialogRef<EnhancedIncomingCallComponent> | null = null;
  private modalResultSubject = new Subject<CallModalResult>();

  // Store pending lead info when socket arrives before modal opens
  private pendingLeadInfo: any = null;

  /**
   * Show enhanced incoming call modal
   */
  showIncomingCall(data: {
    phoneNumber: string;
    callerName?: string;
    leadId?: number;
    callControlId?: string;
    fromAgent?: boolean;
    leadInfo?: any;
    dbCallId?: number;
  }): Observable<CallModalResult> {
    // Suppress panel auto-show during ringing (modal handles this state)
    this.globalCallPanel.suppressForInboundCall();

    // Prevent any default browser notifications or dialogs for incoming calls
    try {
      // Close any existing browser notifications
      if ('Notification' in window) {
        // Close any existing notifications with the same tag
        if (Notification.permission === 'granted') {
          // Browser will automatically close previous notifications with the same tag
        }
      }
    } catch (error) {
      console.warn('Could not manage browser notifications:', error);
    }

    // If there's already an active modal, close it first
    if (this.activeModalRef) {
      this.activeModalRef.close({ reason: 'replaced' });
    }
    // Prepare modal data
    const modalData: EnhancedIncomingCallData = {
      phoneNumber: data.phoneNumber,
      callerName: data.callerName || data.leadInfo?.name,
      leadId: data.leadId || data.leadInfo?.id,
      callSession: {
        id: `call_${Date.now()}`,
        direction: 'inbound',
        phoneNumber: data.phoneNumber,
        startTime: new Date(),
        state: 'ringing',
        telnyxCallId: data.callControlId
      },
      fromAgent: data.fromAgent || false,
      leadInfo: data.leadInfo,
      dbCallId: data.dbCallId
    };

    // Open the modal
    this.activeModalRef = this.dialog.open(EnhancedIncomingCallComponent, {
      width: '420px',
      maxWidth: '95vw',
      height: 'auto',
      maxHeight: 'calc(100vh - 100px)',
      minHeight: '400px',
      disableClose: true,
      hasBackdrop: false,
      backdropClass: 'call-modal-backdrop-clear',
      panelClass: 'call-modal-panel-clear',
      position: {
        top: '50px'
      },
      data: modalData,
      autoFocus: false,
      restoreFocus: false
    });

    // Handle modal close
    this.activeModalRef.afterClosed().subscribe((result: CallModalResult | undefined) => {
      console.log("modal close result", result)
      this.activeModalRef = null;
      this.pendingLeadInfo = null; // Clear pending data when modal closes

      // Stop any audio that might still be playing
      this.audioService.stopRingtone();

      // Emit result
      if (result) {
        this.modalResultSubject.next(result);

        // Handle answered call - capture context and transition to floating panel
        if (result.wasAnswered === true) {
          // Create a session object from the modal data for context capture
          const session = {
            id: modalData.callSession.id,
            direction: 'inbound' as const,
            phoneNumber: result.phoneNumber || data.phoneNumber,
            startTime: modalData.callSession.startTime,
            state: 'connected' as const,
            telnyxCallId: modalData.callSession.telnyxCallId,
            leadId: result.leadId || null
          };

          // Capture inbound call context
          const callerInfo = {
            id: result.leadId,
            name: result.leadInfo?.fullName || data.callerName || 'Unknown Caller',
            phone: result.phoneNumber || data.phoneNumber,
            campus: result.leadInfo?.campus,
            program: result.leadInfo?.program,
            owner: result.leadInfo?.owner
          };

          this.globalCallContext.captureInboundContext(session as any, callerInfo);
          console.log('📞 [EnhancedCallModalService] Captured inbound context for answered call');

          // Transition modal to floating panel
          // Disposition modal will be shown by CallFacadeService when call ends
          this.globalCallPanel.transitionFromInboundModal();
          console.log('📞 [EnhancedCallModalService] Transitioned to floating panel');
        } else {
          // Call was not answered (declined, timeout, etc.) - reset suppression
          this.globalCallPanel.allowAutoShow();
          console.log('📞 [EnhancedCallModalService] Call not answered, allowing auto-show');
        }
      } else {
        // Modal was dismissed without explicit result - reset suppression
        this.globalCallPanel.allowAutoShow();
        this.modalResultSubject.next({ reason: 'dismissed' });
      }
    });

    // Apply any pending lead info that arrived before modal opened
    if (this.pendingLeadInfo) {
      setTimeout(() => {
        this.applyPendingLeadInfo();
      }, 100);
    }

    return this.modalResultSubject.asObservable();
  }

  /**
   * Close the active incoming call modal
   */
  closeIncomingCall(reason: string = 'dismissed'): void {
    if (this.activeModalRef) {
      this.activeModalRef.close({ reason });
    }
  }

  /**
   * Check if there's an active incoming call modal
   */
  hasActiveModal(): boolean {
    return this.activeModalRef !== null;
  }

  /**
   * Get the current modal reference (for advanced operations)
   */
  getActiveModalRef(): MatDialogRef<EnhancedIncomingCallComponent> | null {
    return this.activeModalRef;
  }

  /**
   * Update modal data dynamically (e.g., when lead information is loaded)
   */
  updateModalData(updates: Partial<EnhancedIncomingCallData>): void {
    if (this.activeModalRef) {
      const componentInstance = this.activeModalRef.componentInstance;
      const previousPhoneNumber = componentInstance.data.phoneNumber;

      // Update the data
      Object.assign(componentInstance.data, updates);

      // If phone number was updated and is now valid, trigger lead lookup
      if (updates.phoneNumber &&
          updates.phoneNumber !== previousPhoneNumber &&
          !updates.phoneNumber.startsWith('webrtc')) {
        console.log('📞 [EnhancedCallModalService] Phone number updated, triggering lookup');
        componentInstance.onPhoneNumberUpdated(updates.phoneNumber);
      }
    }
  }

  /**
   * Store pending lead info when socket arrives before modal opens
   * This will be applied when the modal opens via showIncomingCall()
   */
  storePendingLeadInfo(data: any): void {
    console.log('📞 [EnhancedCallModalService] Storing pending lead info (modal not open yet):', data);
    this.pendingLeadInfo = data;
  }

  /**
   * Apply pending lead info to the modal after it opens
   */
  private applyPendingLeadInfo(): void {
    if (!this.pendingLeadInfo || !this.activeModalRef) {
      return;
    }

    const data = this.pendingLeadInfo;
    console.log('📞 [EnhancedCallModalService] Applying pending lead info to modal:', data);

    // Extract lead info from socket data structure
    const leadName = data.lead?.name
      ? `${data.lead.name} ${data.lead.lastName || ''}`.trim()
      : (data.leadName || data.callerName);

    const leadId = data.lead?.id || data.leadId;

    // Extract phone number from correct location
    const phoneNumber = data.call_info?.caller_phone_number
      || data.lead?.phoneNumber
      || data.callerPhone;

    const updates: Partial<EnhancedIncomingCallData> = {};

    if (leadName) {
      updates.callerName = leadName;
    }
    if (leadId) {
      updates.leadId = leadId;
    }
    if (phoneNumber && !phoneNumber.startsWith('webrtc')) {
      updates.phoneNumber = phoneNumber;
    }
    // Pass the database call ID for disposition API
    if (data.call_id) {
      updates.dbCallId = data.call_id;
    }

    if (Object.keys(updates).length > 0) {
      this.updateModalData(updates);
    }

    // Clear pending data after applying
    this.pendingLeadInfo = null;
  }

  /**
   * Show a test modal (for debugging/development)
   */
  showTestModal(): Observable<CallModalResult> {
    const testData = {
      phoneNumber: '+1555123456',
      callerName: 'Test Caller',
      leadId: 123,
      callControlId: 'test-call-' + Date.now(),
      fromAgent: false
    };

    return this.showIncomingCall(testData);
  }

  /**
   * Cleanup method
   */
  cleanup(): void {
    if (this.activeModalRef) {
      this.activeModalRef.close({ reason: 'cleanup' });
    }

    this.modalResultSubject.complete();
    this.audioService.stopRingtone();
  }
}